import type L from "leaflet";
import { ApiError, api, errorText } from "./api";
import { button, byId, clear, el } from "./dom";
import { ratingFields } from "./place";
import { ensureSession } from "./session";
import { closeSheet, openSheet, toast } from "./sheet";
import type { NewPlace, PlaceDetail } from "./types";

export interface AddContext {
  onCreated(place: PlaceDetail): void;
  openPlace(id: string): void;
}

/** Add-place flow: nickname → drop a pin → name + optional first rating. */
export async function startAddPlace(map: L.Map, ctx: AddContext): Promise<void> {
  const me = await ensureSession("Adj meg egy becenevet, hogy helyet vehess fel.");
  if (!me) return;
  closeSheet();
  pickPoint(map, (point) => openForm(map, point, ctx));
}

interface Point {
  lat: number;
  lon: number;
}

/** Shows the crosshair overlay; calls `onPick` with the map centre on confirm. */
function pickPoint(map: L.Map, onPick: (p: Point) => void): void {
  const root = byId("overlay-root");
  clear(root);

  const confirm = button("Itt van 📍", "btn btn--primary btn--wide", () => {
    const c = map.getCenter();
    clear(root);
    document.body.classList.remove("is-picking");
    onPick({ lat: c.lat, lon: c.lng });
  });
  const cancel = button("Mégsem", "btn btn--wide", () => {
    clear(root);
    document.body.classList.remove("is-picking");
  });

  root.append(
    el("div", { class: "crosshair", attrs: { "aria-hidden": "true" } }),
    el("div", { class: "pick-bar" }, [
      el("p", { class: "pick-bar__hint", text: "Told a térképet úgy, hogy a kereszt a hely fölött legyen." }),
      el("div", { class: "pick-bar__actions" }, [cancel, confirm]),
    ]),
  );
  document.body.classList.add("is-picking");
}

function openForm(map: L.Map, point: Point, ctx: AddContext): void {
  const sheet = openSheet("Új hely");

  const name = el("input", {
    class: "input",
    attrs: { type: "text", name: "name", maxlength: "80", required: "", placeholder: "pl. Kis Büfé" },
  });
  const address = el("input", {
    class: "input",
    attrs: { type: "text", name: "address", maxlength: "200", placeholder: "Utca, házszám, város (nem kötelező)" },
  });

  const fields = ratingFields();
  const error = el("div", { class: "form__error", attrs: { role: "alert" } });
  const submit = el("button", { class: "btn btn--primary", type: "submit", text: "Hozzáadom" });

  const coords = el("p", {
    class: "form__note",
    text: `Kiválasztott pont: ${point.lat.toFixed(5)}, ${point.lon.toFixed(5)}`,
  });

  const form = el("form", { class: "form" }, [
    coords,
    button("Másik pontot választok", "btn btn--link", () => {
      closeSheet();
      pickPoint(map, (p) => openForm(map, p, ctx));
    }),
    el("label", { class: "form__label", text: "A hely neve" }),
    name,
    el("label", { class: "form__label", text: "Cím" }),
    address,
    el("h3", { class: "form__section", text: "Értékeled is mindjárt?" }),
    el("p", { class: "form__hint", text: "Kihagyhatod — de ha értékelsz, mindhárom szempont kell." }),
    ...fields.rows,
    fields.commentLabel,
    fields.comment,
    fields.counter,
    error,
    el("div", { class: "form__actions" }, [button("Mégsem", "btn", () => closeSheet()), submit]),
  ]);

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    clear(error);

    const placeName = name.value.trim();
    if (placeName.length < 2 || placeName.length > 80) {
      error.append(el("p", { text: "A hely neve 2–80 karakter legyen." }));
      name.focus();
      return;
    }
    const rating = fields.read();
    if (!rating && fields.touched()) {
      error.append(el("p", { text: "Vagy mindhárom szempontot értékeld, vagy hagyd üresen mind." }));
      return;
    }

    const payload: NewPlace = { name: placeName, lat: point.lat, lon: point.lon };
    const addr = address.value.trim();
    if (addr !== "") payload.address = addr;
    if (rating) payload.rating = rating;

    submit.disabled = true;
    api
      .createPlace(payload)
      .then((created) => {
        toast("Megvan, felkerült a térképre!");
        ctx.onCreated(created);
      })
      .catch((err: unknown) => {
        submit.disabled = false;
        error.append(el("p", { text: errorText(err) }));
        const candidate = duplicateCandidate(err);
        if (candidate) {
          error.append(
            button(`Megnézem: ${candidate.name}`, "btn btn--link", () => ctx.openPlace(candidate.id)),
          );
        }
      });
  });

  clear(sheet.body);
  sheet.body.append(form);
  name.focus();
}

/** Pulls the existing place out of a 409 duplicate_place response. */
function duplicateCandidate(err: unknown): { id: string; name: string } | null {
  if (!(err instanceof ApiError) || err.code !== "duplicate_place") return null;
  const c = err.details?.["candidate"];
  if (typeof c !== "object" || c === null) return null;
  const { id, name } = c as { id?: unknown; name?: unknown };
  if (typeof id !== "string" || typeof name !== "string") return null;
  return { id, name };
}
